import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import _ from 'lodash'
import { useSnackbar } from 'notistack'

import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import { tableHeadSx } from '../../common/sharedStyles'
import {
  createOffer, errText, getOffer, listOffers, listPlans, publishOffer, updateOffer, withdrawOffer,
} from './api'
import { AUDIENCE, OFFER_STATUS, PAYMENT_MODES, inr, overdraftLabel, statusColor, unlimitedOr } from './constants'

/**
 * ProShip Direct — offers.
 *
 * An offer is a priced deal. Two audiences:
 *   PUBLIC   — every merchant sees it (a time-boxed promotion), optionally capped by maxAcceptances.
 *   TARGETED — only the merchants it is published to, which is the KAM negotiation path.
 *
 * The terms live ONCE on the offer; who received it and what each of them did lives on the
 * per-merchant target rows, because one offer published to five merchants has five independent
 * outcomes. That is why "Publish" takes a list and why the detail view shows a response table
 * rather than a single status.
 *
 * Publishing again with more merchants ADDS them — existing recipients and their answers are
 * untouched (the backend upserts on the unique (offer, merchant) pair).
 */
export default function Offers() {
  const { enqueueSnackbar } = useSnackbar()
  const [rows, setRows] = useState([])
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(null)
  const [publishing, setPublishing] = useState(null)
  const [viewing, setViewing] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [o, p] = await Promise.all([listOffers(), listPlans()])
      setRows(Array.isArray(o?.data) ? o.data : [])
      setPlans(Array.isArray(p?.data) ? p.data : [])
    } catch (e) {
      enqueueSnackbar(errText(e), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const doWithdraw = async (id) => {
    try {
      await withdrawOffer(id)
      enqueueSnackbar('Offer withdrawn. Merchants who had not responded can no longer see it.', { variant: 'success' })
      load()
    } catch (e) { enqueueSnackbar(errText(e), { variant: 'error' }) }
  }

  const columns = [
    { field: 'offerNo', headerName: 'Offer', minWidth: 170, flex: 1 },
    { field: 'name', headerName: 'Label', minWidth: 170, flex: 1.2 },
    {
      field: 'audience', headerName: 'Audience', minWidth: 110, flex: 0.7,
      renderCell: ({ row }) => (
        <Chip size="small" label={row.audience}
              color={row.audience === AUDIENCE.PUBLIC ? 'info' : 'default'} />
      ),
    },
    { field: 'planCode', headerName: 'Plan', minWidth: 100, flex: 0.6 },
    {
      field: 'totalPayable', headerName: 'Payable', minWidth: 120, flex: 0.8,
      renderCell: ({ row }) => inr(row.totalPayable),
    },
    {
      field: 'shipmentsIncluded', headerName: 'Shipments', minWidth: 110, flex: 0.7,
      renderCell: ({ row }) => unlimitedOr(row.shipmentsIncluded),
    },
    {
      field: 'status', headerName: 'Status', minWidth: 120, flex: 0.7,
      renderCell: ({ row }) => <Chip size="small" label={row.status} color={statusColor(row.status)} />,
    },
    {
      field: 'acceptedCount', headerName: 'Accepted', minWidth: 110, flex: 0.6,
      renderCell: ({ row }) =>
        row.maxAcceptances ? `${row.acceptedCount ?? 0} / ${row.maxAcceptances}` : (row.acceptedCount ?? 0),
    },
    {
      field: 'offerValidTill', headerName: 'Valid till', minWidth: 130, flex: 0.8,
      renderCell: ({ row }) =>
        row.offerValidTill ? new Date(row.offerValidTill).toLocaleDateString('en-IN') : 'Open-ended',
    },
    {
      field: 'actions', headerName: 'Actions', minWidth: 420, flex: 1.8, sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />}
                  onClick={() => setViewing(row.id)}>View</Button>
          {/* Only a draft can change: once published, merchants may have seen or accepted its terms. */}
          {row.status === OFFER_STATUS.DRAFT && (
            <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />}
                    onClick={() => setEditing(row)}>Edit</Button>
          )}
          {/*
            Once published, a PUBLIC offer has nothing left to publish, so the button greys out. A
            TARGETED one can still go to more merchants, and says so.
          */}
          {row.status === OFFER_STATUS.PUBLISHED && row.audience === AUDIENCE.PUBLIC ? (
            <Tooltip title="Already live for every merchant">
              <span>
                <Button size="small" variant="contained" startIcon={<SendOutlinedIcon />} disabled>
                  Published
                </Button>
              </span>
            </Tooltip>
          ) : row.status !== OFFER_STATUS.WITHDRAWN && (
            <Button size="small" variant="contained" startIcon={<SendOutlinedIcon />}
                    onClick={() => setPublishing(row)}>
              {row.status === OFFER_STATUS.PUBLISHED ? 'Add merchants' : 'Publish'}
            </Button>
          )}
          {row.status === OFFER_STATUS.PUBLISHED && (
            <Tooltip title="Pulls it back. Acceptances already made still stand.">
              <Button size="small" color="error" startIcon={<BlockOutlinedIcon />}
                      onClick={() => doWithdraw(row.id)}>Withdraw</Button>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ]

  return (
    <Page>
      <PageTitle>ProShip Direct — Offers</PageTitle>

      <Alert severity="info" sx={{ mb: 2 }}>
        A <strong>PUBLIC</strong> offer is visible to every merchant. A <strong>TARGETED</strong> offer is
        visible only to the merchants you publish it to — that is the KAM negotiation path.
      </Alert>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New offer
        </Button>
      </Box>

      <ProzoDataGrid
        rows={rows} columns={columns} loading={loading} paginationMode="client"
        rowCount={rows.length} page={page} setPage={setPage}
        sizePerPage={sizePerPage} setSizePerPage={setSizePerPage}
        getRowId={(r) => r.id} disableColumnMenu
      />

      {(creating || editing) && (
        <OfferDialog plans={plans} offer={editing}
                     onClose={() => { setCreating(false); setEditing(null) }}
                     onSaved={() => { setCreating(false); setEditing(null); load() }}
                     onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                     onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
      {publishing && (
        <PublishDialog offer={publishing} onClose={() => setPublishing(null)}
                       onDone={() => { setPublishing(null); load() }}
                       onError={(m) => enqueueSnackbar(m, { variant: 'error' })}
                       onSuccess={(m) => enqueueSnackbar(m, { variant: 'success' })} />
      )}
      {viewing && <OfferDetailDialog offerId={viewing} onClose={() => setViewing(null)} />}
    </Page>
  )
}

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v))
const str = (v) => (v === null || v === undefined ? '' : String(v))

const TERM_FIELDS = ['planFee', 'shipmentsIncluded', 'periodDays', 'overagePrice', 'overdraftLimitUnits',
  'graceDays', 'courierIntegrationLimit']

/**
 * Build a deal: seed from a plan, then override only what was negotiated. With `offer` it edits that
 * draft instead, starting from its current terms; switching the seed plan clears the terms so the
 * new plan's values apply rather than the old plan's numbers.
 */
function OfferDialog({ plans, offer, onClose, onSaved, onError, onSuccess }) {
  const editing = Boolean(offer)
  const [saving, setSaving] = useState(false)
  const [planId, setPlanId] = useState(offer?.planId || '')
  const [f, setF] = useState({
    name: str(offer?.name), audience: offer?.audience || AUDIENCE.TARGETED,
    // datetime-local wants YYYY-MM-DDTHH:mm
    offerValidTill: offer?.offerValidTill ? String(offer.offerValidTill).slice(0, 16) : '',
    maxAcceptances: str(offer?.maxAcceptances), notes: str(offer?.notes),
    ...Object.fromEntries(TERM_FIELDS.map((k) => [k, str(offer?.[k])])),
    paymentMode: offer?.paymentMode || 'WALLET',
  })
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }))
  const seed = useMemo(() => plans.find((p) => p.id === planId), [plans, planId])
  const choosePlan = (id) => {
    setPlanId(id)
    if (editing) setF((p) => ({ ...p, ...Object.fromEntries(TERM_FIELDS.map((k) => [k, ''])) }))
  }

  const submit = async () => {
    setSaving(true)
    try {
      const body = {
        name: f.name,
        audience: f.audience,
        planId: planId || null,
        offerValidTill: f.offerValidTill || null,
        maxAcceptances: numOrNull(f.maxAcceptances),
        notes: f.notes,
        // Only non-empty fields become overrides; everything else keeps the plan's default.
        terms: {
          planFee: numOrNull(f.planFee),
          shipmentsIncluded: numOrNull(f.shipmentsIncluded),
          periodDays: numOrNull(f.periodDays),
          overagePrice: numOrNull(f.overagePrice),
          overdraftLimitUnits: numOrNull(f.overdraftLimitUnits),
          graceDays: numOrNull(f.graceDays),
          courierIntegrationLimit: numOrNull(f.courierIntegrationLimit),
          paymentMode: f.paymentMode,
        },
      }
      if (editing) {
        await updateOffer(offer.id, body)
        onSuccess(`${offer.offerNo} updated. It stays a draft until you publish it.`)
      } else {
        await createOffer(body)
        onSuccess('Offer created as DRAFT. Publish it to make it visible.')
      }
      onSaved()
    } catch (e) { onError(errText(e)) } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{editing ? `Edit ${offer.offerNo}` : 'New offer'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Internal label" value={f.name} onChange={set('name')} fullWidth size="small"
                       placeholder="Diwali 2026 — Growth at 12k" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Audience" value={f.audience} onChange={set('audience')} fullWidth size="small">
              <MenuItem value={AUDIENCE.TARGETED}>Targeted</MenuItem>
              <MenuItem value={AUDIENCE.PUBLIC}>Public</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Seed from plan" value={planId}
                       onChange={(e) => choosePlan(e.target.value)} fullWidth size="small">
              <MenuItem value="">Bespoke (no plan)</MenuItem>
              {plans.map((p) => <MenuItem key={p.id} value={p.id}>{p.planCode}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {seed && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Seeded from <strong>{seed.planCode}</strong>: {inr(seed.planFee)} ex-GST,{' '}
            {unlimitedOr(seed.shipmentsIncluded)} shipments, overdraft {overdraftLabel(seed.overdraftLimitUnits)}.
            Leave a field blank below to keep the plan&apos;s value.
            {editing && planId !== (offer.planId || '') ? ' Terms were reset to this plan; re-enter anything negotiated.' : ''}
          </Alert>
        )}
        {!seed && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No plan selected, so nothing is seeded — at minimum set the fee.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Negotiated terms (blank = keep plan default)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}><TextField label="Fee (ex-GST)" type="number" value={f.planFee} onChange={set('planFee')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Shipments" type="number" value={f.shipmentsIncluded} onChange={set('shipmentsIncluded')} fullWidth size="small" helperText="Blank = plan default" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Period (days)" type="number" value={f.periodDays} onChange={set('periodDays')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Overage / shipment" type="number" value={f.overagePrice} onChange={set('overagePrice')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Overdraft units" type="number" value={f.overdraftLimitUnits} onChange={set('overdraftLimitUnits')} fullWidth size="small" helperText="0 = hard stop" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Grace days" type="number" value={f.graceDays} onChange={set('graceDays')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}><TextField label="Courier accounts" type="number" value={f.courierIntegrationLimit} onChange={set('courierIntegrationLimit')} fullWidth size="small" /></Grid>
          <Grid item xs={12} sm={3}>
            <TextField select label="Payment mode" value={f.paymentMode} onChange={set('paymentMode')} fullWidth size="small">
              {PAYMENT_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Offer valid till" type="datetime-local" value={f.offerValidTill}
                       onChange={set('offerValidTill')} fullWidth size="small" InputLabelProps={{ shrink: true }}
                       helperText="Blank = open-ended" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Max acceptances" type="number" value={f.maxAcceptances}
                       onChange={set('maxAcceptances')} fullWidth size="small"
                       helperText="Caps a public promotion. Blank = uncapped" />
          </Grid>
          <Grid item xs={12}>
            <TextField label="Notes (merchant-visible)" value={f.notes} onChange={set('notes')}
                       fullWidth size="small" multiline rows={2}
                       placeholder="What was negotiated and why" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : editing ? 'Save draft' : 'Create draft'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Publish: TARGETED needs recipients, PUBLIC goes live for everyone. */
function PublishDialog({ offer, onClose, onDone, onError, onSuccess }) {
  const isTargeted = offer.audience === AUDIENCE.TARGETED
  const { data: merchants = [], isFetching } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [picked, setPicked] = useState([])
  const [saving, setSaving] = useState(false)
  const options = useMemo(() => _.orderBy(merchants.filter((m) => m.is_deleted === '0'), 'name'), [merchants])

  const submit = async () => {
    if (isTargeted && picked.length === 0) { onError('Pick at least one merchant.'); return }
    setSaving(true)
    try {
      await publishOffer(offer.id, {
        merchants: picked.map((m) => ({ merchantId: m.merchantId, merchantOid: m.id })),
      })
      onSuccess(isTargeted
        ? `Published to ${picked.length} merchant${picked.length > 1 ? 's' : ''}.`
        : 'Offer is now live for every merchant.')
      onDone()
    } catch (e) { onError(errText(e)) } finally { setSaving(false) }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Publish {offer.offerNo}</DialogTitle>
      <DialogContent dividers>
        {isTargeted ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Each merchant responds independently. Publishing again later adds merchants without
              disturbing anyone who has already answered.
            </Alert>
            <Autocomplete
              multiple options={options} value={picked} loading={isFetching}
              getOptionLabel={(m) => (m.companyName ? `${m.name} (${m.companyName})` : m.name) || ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_e, v) => setPicked(v)}
              renderInput={(p) => <TextField {...p} label="Merchants" placeholder="Search merchant…" size="small" />}
            />
          </>
        ) : (
          <Alert severity="warning">
            This is a PUBLIC offer — publishing makes it visible to <strong>every</strong> merchant
            {offer.maxAcceptances ? `, capped at ${offer.maxAcceptances} acceptances` : ''}.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>{saving ? 'Publishing…' : 'Publish'}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** Who got it, who opened it, who said what. */
function OfferDetailDialog({ offerId, onClose }) {
  const [offer, setOffer] = useState(null)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    getOffer(offerId)
      .then((r) => { if (alive) setOffer(r?.data) })
      .catch((e) => { if (alive) setErr(errText(e)) })
    return () => { alive = false }
  }, [offerId])

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle>{offer?.offerNo || 'Offer'}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error">{err}</Alert>}
        {!offer && !err && <Typography variant="body2">Loading…</Typography>}
        {offer && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
              <Chip size="small" label={offer.audience} />
              <Chip size="small" label={offer.status} color={statusColor(offer.status)} />
              <Chip size="small" label={`${inr(offer.totalPayable)} payable`} />
              <Chip size="small" label={`${unlimitedOr(offer.shipmentsIncluded)} shipments`} />
              <Chip size="small" label={`Overdraft ${overdraftLabel(offer.overdraftLimitUnits)}`} />
            </Stack>
            {offer.notes && <Alert severity="info" sx={{ mb: 2 }}>{offer.notes}</Alert>}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Recipients</Typography>
            {(!offer.targets || offer.targets.length === 0) ? (
              <Typography variant="body2" color="text.secondary">
                {offer.audience === AUDIENCE.PUBLIC
                  ? 'No one has taken this promotion yet. A row appears here when a merchant accepts.'
                  : 'Not published to anyone yet.'}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead sx={tableHeadSx}>
                  <TableRow>
                    <TableCell>Merchant</TableCell><TableCell>Status</TableCell>
                    <TableCell>Viewed</TableCell><TableCell>Responded</TableCell><TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offer.targets.map((t) => (
                    <TableRow key={`${t.merchantId}`}>
                      <TableCell>{t.merchantOid || t.merchantId}</TableCell>
                      <TableCell><Chip size="small" label={t.status} color={statusColor(t.status)} /></TableCell>
                      <TableCell>{t.viewedAt ? new Date(t.viewedAt).toLocaleString('en-IN') : '—'}</TableCell>
                      <TableCell>{t.respondedAt ? new Date(t.respondedAt).toLocaleString('en-IN') : '—'}</TableCell>
                      <TableCell>{t.declinedReason || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  )
}
